import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { Button } from "components/ui/button";
import { AdApplicationInterface, AdApplyStatus } from "types/ad";
import { getApplicationConversationUrl } from "helpers/application/getApplicationConversationUrl";
import { IntlContext } from "context";

const statusLabelId: Record<AdApplyStatus, string> = {
    [AdApplyStatus.PENDING]: "profile.my_applications.status.pending",
    [AdApplyStatus.HIRED]: "profile.my_applications.status.accepted",
    [AdApplyStatus.ACCEPTED]: "profile.my_applications.status.accepted",
    [AdApplyStatus.REFUSED]: "profile.my_applications.status.refused",
    [AdApplyStatus.CANCELLED]: "profile.my_applications.status.cancelled",
    [AdApplyStatus.EXPIRED]: "profile.my_applications.status.expired",
};

const statusClassName: Record<AdApplyStatus, string> = {
    [AdApplyStatus.PENDING]: "bg-amber-50 text-amber-700",
    [AdApplyStatus.HIRED]: "bg-emerald-50 text-emerald-700",
    [AdApplyStatus.ACCEPTED]: "bg-emerald-50 text-emerald-700",
    [AdApplyStatus.REFUSED]: "bg-rose-50 text-rose-700",
    [AdApplyStatus.CANCELLED]: "bg-slate-100 text-slate-600",
    [AdApplyStatus.EXPIRED]: "bg-slate-100 text-slate-600",
};

const currencyFormatter = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });

type ApplicationCardProps = {
    application: AdApplicationInterface;
};

const ApplicationCard: React.FC<ApplicationCardProps> = ({ application }) => {
    const intl = useContext(IntlContext);
    const sentAt = new Date(application.created_at).toLocaleDateString("fr-FR");
    const startDate = application.start_date
        ? new Date(application.start_date).toLocaleDateString("fr-FR")
        : null;
    const offerConversationUrl = getApplicationConversationUrl(application);
    const applicationTitle = application.ad?.title ?? intl.formatMessage("profile.my_applications.direct_contact");

    return (
        <article className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="truncate text-base font-bold text-gray-900">{applicationTitle}</h3>
                    <p className="mt-1 text-sm text-slate-500">
                        {currencyFormatter.format(Number(application.amount))} ·{" "}
                        {intl.formatMessage("profile.my_applications.duration_hours", {
                            duration: Number(application.duration).toString(),
                        })}{" "}
                        ·{" "}
                        {intl.formatMessage("profile.my_applications.hourly_rate", {
                            rate: Number(application.hourly_rate).toFixed(2),
                        })}
                    </p>
                    {startDate && (
                        <p className="mt-1 text-sm text-slate-500">
                            {application.start_time
                                ? intl.formatMessage("profile.my_applications.start_date_time", {
                                      date: startDate,
                                      time: application.start_time.slice(0, 5),
                                  })
                                : intl.formatMessage("profile.my_applications.start_date", { date: startDate })}
                        </p>
                    )}
                </div>
                <span
                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusClassName[application.status]}`}
                >
                    {intl.formatMessage(statusLabelId[application.status])}
                </span>
            </div>

            {application.message && <p className="mt-3 text-sm text-slate-600">{application.message}</p>}

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <span className="text-xs text-slate-500">
                    {intl.formatMessage("profile.my_applications.sent_at", { date: sentAt })}
                </span>
                <Button asChild variant="outline" size="sm">
                    <Link to={offerConversationUrl} className="inline-flex items-center gap-1.5">
                        {intl.formatMessage("profile.my_applications.view_proposed_offer")}
                        <MessageCircle className="h-3.5 w-3.5" aria-hidden />
                    </Link>
                </Button>
            </div>
        </article>
    );
};

export default ApplicationCard;
